import { useEffect, useState } from "react";
import { Cartao, Tabela, quantidadeComUnidade } from "@plantus/ui";
import type { Coluna } from "@plantus/ui";
import { producao } from "@plantus/api-client";
import type { QuarentenaPorMateriaPrima } from "@plantus/api-client";

/**
 * Quanto de cada insumo chegou e está parado esperando decisão.
 *
 * <p><b>Não é saldo de estoque</b>, e o título diz isso. Saldo é o que entrou
 * menos o que saiu; não há saída no sistema ainda — nem dispensação, nem
 * pesagem, nem baixa por amostragem.
 *
 * <p>Chamar esta soma de "saldo" seria correto hoje e errado no dia seguinte ao
 * primeiro consumo, sem que nada quebrasse para avisar. Num sistema onde
 * quantidade de insumo é dado regulado, número com rótulo errado é pior que
 * número nenhum: alguém decide com ele.
 */
export function ResumoDaQuarentena({ empresaId }: { empresaId: string }) {
  const [linhas, definirLinhas] = useState<QuarentenaPorMateriaPrima[]>([]);

  useEffect(() => {
    void producao.quarentena(empresaId).then(definirLinhas).catch(() => undefined);
  }, [empresaId]);

  if (linhas.length === 0) {
    return null;
  }

  return (
    <Cartao titulo="Em quarentena">
      <h2 className="quarentena__titulo">Aguardando decisão da qualidade</h2>
      <p className="quarentena__apoio">
        O que chegou e ainda não foi liberado. <strong>Não é saldo de estoque</strong> —
        não há dispensação nem baixa no sistema, então nada saiu ainda.
      </p>

      <Tabela
        colunas={COLUNAS}
        linhas={linhas}
        chave={(linha) => linha.materiaPrimaId}
      />
    </Cartao>
  );
}

/**
 * A matéria-prima é o que identifica a linha — vira o título do cartão no
 * celular. Sem `principal`, o cartão abriria com "Matéria-prima: Óleo de
 * copaíba", gastando a linha mais visível com a palavra menos útil.
 */
const COLUNAS: Coluna<QuarentenaPorMateriaPrima>[] = [
  {
    chave: "nome",
    rotulo: "Matéria-prima",
    principal: true,
    conteudo: (l) => (
      <span>
        {l.nome}
        <small className="lotes__sub">{l.codigo}</small>
      </span>
    ),
  },
  {
    chave: "quantidade",
    rotulo: "Quantidade",
    numerica: true,
    // Aqui é uma SOMA de lotes, e é o número que se usa para decidir se há
    // material bastante para uma formulação. Ele saía como `120.000000 kg` —
    // cento e vinte mil quilos, em português. Ver `quantidade.ts`.
    conteudo: (l) => quantidadeComUnidade(l.quantidade, l.unidade),
  },
  {
    chave: "lotes",
    rotulo: "Lotes",
    numerica: true,
    // Um tambor ou vinte são situações diferentes no chão.
    conteudo: (l) => (
      <span>
        {l.lotes}
        {l.vencidos > 0 && (
          <small className="quarentena__vencidos">{l.vencidos} vencido(s)</small>
        )}
      </span>
    ),
  },
  {
    chave: "validade",
    rotulo: "Vence primeiro",
    conteudo: (l) => (
      <span className={l.vencidos > 0 ? "lotes__vencido" : undefined}>
        {formatar(l.validadeMaisProxima)}
      </span>
    ),
  },
];

/** Data do calendário, sem passar por fuso. Ver Etiqueta.tsx. */
function formatar(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}
